/* eslint-disable max-lines */
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';

import { useWebSockets } from '@/components/providers/WebsocketsProvider';
import { Button } from '@/components/ui/button';
import { preLoadIntegrations } from '@/hooks/useIntegrations';
import { callAnnieRCA, startIncidentResolution } from '@/lib/queries/chat';
import { preLoadDefaultProject } from '@/lib/queries/project';
import { rcaIncidentResolutionsQueries } from '@/lib/queries/rca/incidentResolutions';
import { incidentsManagementPagerDutyIncidentsQueries } from '@/lib/queries/rca/incidents';
import { generateMutationKey } from '@/lib/queries/utils';

import { ChatInterface } from './-components/ChatInterface';
import { IncidentAlert } from './-components/IncidentAlert';
import { InvestigationTimeline } from './-components/InvestigationTimeline';

export const Route = createFileRoute('/_root/rca/$incidentId/')({
  component: RouteComponent,
  beforeLoad: async ({ context, params }) => {
    const project = await preLoadDefaultProject({ context });

    // Load incidents to get the title for breadcrumb
    const incidents = await context.queryClient.ensureQueryData(
      incidentsManagementPagerDutyIncidentsQueries.list(project.id),
    );

    const incident = incidents.find(inc => inc.id === params.incidentId);

    // TODO - Use parser instaed of this weird if statement
    // Get the title from the first available source
    let incidentTitle = `Incident ${params.incidentId}`;
    if (incident?.pagerDutyIncident?.title !== undefined) {
      incidentTitle = incident.pagerDutyIncident.title;
    } else if (incident?.slackIncident?.messageText !== undefined) {
      incidentTitle = incident.slackIncident.messageText;
    }

    return {
      title: `${incidentTitle} - Root Cause Analysis`,
    };
  },
  loader: async ({ context, params }) => {
    const project = await preLoadDefaultProject({ context });

    preLoadIntegrations(project.id)({ context });

    await Promise.all([
      context.queryClient.ensureQueryData(
        incidentsManagementPagerDutyIncidentsQueries.list(project.id),
      ),
      context.queryClient.ensureQueryData(
        rcaIncidentResolutionsQueries.list(project.id, params.incidentId),
      ),
    ]);

    return { project, incidentId: params.incidentId };
  },
});

function RouteComponent() {
  const { project, incidentId } = Route.useLoaderData();

  const { data: incidents } = useSuspenseQuery(
    incidentsManagementPagerDutyIncidentsQueries.list(project.id, 3000),
  );

  const { addMessageHandler, removeMessageHandler } = useWebSockets();
  const queryClient = useQueryClient();

  useEffect(() => {
    const handleRCAError = ({ content }: { content: string }) => {
      const parsedContent = JSON.parse(content) as {
        incidentId: string;
      };

      queryClient.invalidateQueries(
        rcaIncidentResolutionsQueries.list(
          project.id,
          parsedContent.incidentId,
        ),
      );
    };

    const handleRCAAnswer = ({ content }: { content: string }) => {
      const parsedContent = JSON.parse(content) as {
        incidentId: string;
      };

      queryClient.invalidateQueries(
        rcaIncidentResolutionsQueries.list(
          project.id,
          parsedContent.incidentId,
        ),
      );
    };

    addMessageHandler('rca-error', handleRCAError);
    addMessageHandler('rca-answer', handleRCAAnswer);

    return () => {
      removeMessageHandler('rca-error');
      removeMessageHandler('rca-answer');
    };
  }, [addMessageHandler, removeMessageHandler, project.id, queryClient]);

  const incident = incidents.find(inc => inc.id === incidentId);

  const { data: rcaResolutions } = useSuspenseQuery(
    rcaIncidentResolutionsQueries.list(project.id, incidentId),
  );

  const incidentResolution = rcaResolutions[0];

  const { mutateAsync } = useMutation({
    mutationKey: generateMutationKey(
      rcaIncidentResolutionsQueries.list(project.id, incidentId),
      'rcaUserChat',
      true,
    ),
    mutationFn: async (message: string) => {
      await callAnnieRCA({
        projectId: project.id,
        prompt: message,
        history: [],
        incidentResolutionId:
          incidentResolution?.id ?? 'NO_INCIDENT_RESOLUTION_ID',
        incidentId: incidentId,
      });
    },
  });

  const { mutateAsync: startIncidentResolutionMutation } = useMutation({
    mutationKey: generateMutationKey(
      rcaIncidentResolutionsQueries.list(project.id, incidentId),
      'startIncidentResolution',
      true,
    ),
    mutationFn: async () => {
      await startIncidentResolution({
        projectId: project.id,
        incidentId: incidentId,
      });
    },
  });

  if (!incident) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
          <FormattedMessage id="rca.details.incidentNotFound" />
        </h1>
        <Button variant="outline" onClick={() => window.history.back()}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          <FormattedMessage id="rca.details.goBack" />
        </Button>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex justify-center mb-6">
        <div className="flex items-center justify-between w-full max-w-7xl">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => window.history.back()}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              <FormattedMessage id="common.back" />
            </Button>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
              {incident.pagerDutyIncident?.title !== undefined ? (
                incident.pagerDutyIncident.title
              ) : incident.slackIncident?.messageText !== undefined ? (
                incident.slackIncident.messageText
              ) : (
                <FormattedMessage id="rca.details.incidentDetails" />
              )}
            </h1>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex justify-center min-h-0">
        <div className="flex gap-6 w-full max-w-7xl">
          {/* Left Side - Original Alert (Compact) */}
          <div className="w-80 flex-shrink-0 flex flex-col gap-4">
            <IncidentAlert
              incident={incident}
              startIncidentResolution={startIncidentResolutionMutation}
              incidentResolution={incidentResolution}
            />
            <div className="sticky top-6">
              <ChatInterface onSendMessage={mutateAsync} />
            </div>
          </div>

          {/* Middle - Investigation Timeline */}
          <div className="flex-1 min-w-0">
            {/* <Integrations defaultProjectId={project.id} /> */}
            {incidentResolution ? (
              <InvestigationTimeline incidentResolution={incidentResolution} />
            ) : (
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 text-center">
                <p className="text-gray-600 dark:text-gray-400">
                  <FormattedMessage id="rca.details.noInvestigation" />
                </p>
                <Button
                  variant="main"
                  className="mt-4"
                  onClick={() => startIncidentResolutionMutation()}
                >
                  Start Investigation
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
